/**
 * Fase 4 — Inline-opprettelse av vurderingsmatrise fra prøvesett-veiviseren.
 *
 * Åpnes som modal-i-modal fra RubricStep. Lager én matrise via
 * POST /api/admin/assessment-rubrics og legger til kriterier via
 * POST /api/admin/assessment-rubrics/:id/criteria. Returnerer rubric_id til
 * veiviseren slik at det kan brukes direkte.
 *
 * MVP: Krever 1+ kriterium og 2+ nivåer per kriterium. For dypere redigering
 * (lås, versjonering) henvises bruker til egen vurderingsmatrise-side.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Save, Loader2, AlertCircle, Info } from 'lucide-react';
import { authenticatedFetch } from '../../lib/auth';
import { useToast } from '../ui';

type ComponentType = 'written' | 'oral' | 'practical' | 'observation';

const COMPONENT_LABELS: Record<ComponentType, string> = {
  written: 'Skriftlig prøve',
  oral: 'Muntlig',
  practical: 'Praktisk demonstrasjon',
  observation: 'Kontinuerlig observasjon',
};

interface CriterionLevel {
  name: string;
  points: number;
  description: string;
}

interface CriterionDraft {
  title: string;
  description: string;
  weight_pct: number;
  levels: CriterionLevel[];
}

function emptyLevels(): CriterionLevel[] {
  return [
    { name: 'Ikke bestått', points: 0, description: '' },
    { name: 'Bestått', points: 1, description: '' },
  ];
}

function emptyCriterion(): CriterionDraft {
  return { title: '', description: '', weight_pct: 100, levels: emptyLevels() };
}

export interface InlineCreateRubricModalProps {
  open: boolean;
  onClose: () => void;
  /** Standard component type ut fra valgt vurderingsform */
  defaultComponentType?: ComponentType;
  /** memberId som matrisen skal tilhøre (ignoreres for admin/systemadmin som sender eget member_id) */
  memberIdForAdmin?: string;
  /** Kalt med ny rubric_id når lagringen er ferdig */
  onCreated: (rubricId: string) => void;
}

export default function InlineCreateRubricModal({
  open,
  onClose,
  defaultComponentType = 'practical',
  memberIdForAdmin,
  onCreated,
}: InlineCreateRubricModalProps) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [componentTypes, setComponentTypes] = useState<ComponentType[]>([defaultComponentType]);
  const [criteria, setCriteria] = useState<CriterionDraft[]>([emptyCriterion()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle('');
    setDescription('');
    setComponentTypes([defaultComponentType]);
    setCriteria([emptyCriterion()]);
    setError(null);
    setSaving(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const toggleComponent = (t: ComponentType) => {
    setComponentTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  const updateCriterion = (idx: number, patch: Partial<CriterionDraft>) => {
    setCriteria((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const addCriterion = () => setCriteria((prev) => [...prev, emptyCriterion()]);
  const removeCriterion = (idx: number) =>
    setCriteria((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const addLevel = (ci: number) =>
    setCriteria((prev) =>
      prev.map((c, i) =>
        i === ci
          ? {
              ...c,
              levels: [...c.levels, { name: '', points: c.levels.length, description: '' }],
            }
          : c,
      ),
    );
  const removeLevel = (ci: number, li: number) =>
    setCriteria((prev) =>
      prev.map((c, i) =>
        i === ci && c.levels.length > 2
          ? { ...c, levels: c.levels.filter((_, j) => j !== li) }
          : c,
      ),
    );
  const updateLevel = (ci: number, li: number, patch: Partial<CriterionLevel>) =>
    setCriteria((prev) =>
      prev.map((c, i) =>
        i === ci ? { ...c, levels: c.levels.map((l, j) => (j === li ? { ...l, ...patch } : l)) } : c,
      ),
    );

  const validate = (): string | null => {
    if (!title.trim() || title.trim().length < 3) return 'Tittel må være minst 3 tegn.';
    if (componentTypes.length === 0) return 'Velg minst én vurderingsform matrisen skal brukes for.';
    for (let i = 0; i < criteria.length; i++) {
      const c = criteria[i];
      if (!c.title.trim() || c.title.trim().length < 2)
        return `Kriterium ${i + 1} må ha tittel (minst 2 tegn).`;
      if (c.levels.length < 2) return `Kriterium ${i + 1} må ha minst 2 nivåer.`;
      for (let j = 0; j < c.levels.length; j++) {
        if (!c.levels[j].name.trim()) return `Kriterium ${i + 1} nivå ${j + 1} må ha navn.`;
      }
    }
    const totalWeight = criteria.reduce((s, c) => s + (c.weight_pct || 0), 0);
    if (Math.abs(totalWeight - 100) > 0.01 && criteria.length > 1) {
      return `Vektene må summere til 100% (nå: ${totalWeight.toFixed(0)}%).`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSaving(true);

    try {
      // 1. Opprett rubric
      const createRes = await authenticatedFetch('/api/admin/assessment-rubrics', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          complexity_mode: 'graded',
          applicable_component_types: componentTypes,
          member_id: memberIdForAdmin, // ignoreres av backend for member-role
        }),
      });
      const createJson = await createRes.json();
      if (!createRes.ok || !createJson.success) {
        throw new Error(createJson.error || createJson.message || 'Kunne ikke opprette matrise');
      }
      const rubricId: string = createJson.rubric.id;

      // 2. Legg til kriterier
      for (let i = 0; i < criteria.length; i++) {
        const c = criteria[i];
        const critRes = await authenticatedFetch(`/api/admin/assessment-rubrics/${rubricId}/criteria`, {
          method: 'POST',
          body: JSON.stringify({
            title: c.title.trim(),
            description: c.description.trim() || undefined,
            weight_pct: c.weight_pct,
            levels: c.levels.map((l) => ({
              name: l.name.trim(),
              points: l.points,
              description: l.description.trim() || undefined,
            })),
            sequence: i,
          }),
        });
        const critJson = await critRes.json();
        if (!critRes.ok || !critJson.success) {
          throw new Error(critJson.error || critJson.message || `Kunne ikke lagre kriterium ${i + 1}`);
        }
      }

      toast.showSuccess('Ny vurderingsmatrise opprettet.');
      onCreated(rubricId);
      reset();
      onClose();
    } catch (err_: unknown) {
      const msg = err_ instanceof Error ? err_.message : 'Ukjent feil';
      setError(msg);
      toast.showError('Opprettelse feilet: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-stretch sm:items-center justify-center sm:p-4 bg-kf-gray-900/60 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[88vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inline-rubric-title"
      >
        <header className="px-4 sm:px-6 py-4 border-b border-kf-gray-200 bg-emerald-50 sm:rounded-t-xl flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="inline-rubric-title" className="text-lg font-semibold text-kf-gray-900">
                Opprett ny vurderingsmatrise
              </h2>
              <p className="text-sm text-kf-gray-700 mt-1">
                Rask opprettelse — du kan utdype matrisen senere via Innstillinger ▸ Vurderingsmatriser.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="shrink-0 p-1.5 rounded hover:bg-white text-kf-gray-600"
              aria-label="Lukk"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Tittel */}
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">
              Tittel på matrisen <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="F.eks: Praktisk mureprøve"
              className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Beskrivelse */}
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">Beskrivelse</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Kort forklaring av hva matrisen vurderer…"
              className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Component types */}
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-2">
              Brukes for vurderingsform <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(COMPONENT_LABELS) as ComponentType[]).map((t) => (
                <label
                  key={t}
                  className={`px-3 py-1.5 rounded border text-sm cursor-pointer transition-colors ${
                    componentTypes.includes(t)
                      ? 'bg-emerald-100 border-emerald-400 text-emerald-900 font-medium'
                      : 'bg-white border-kf-gray-300 text-kf-gray-700 hover:bg-kf-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={componentTypes.includes(t)}
                    onChange={() => toggleComponent(t)}
                    className="mr-1.5"
                  />
                  {COMPONENT_LABELS[t]}
                </label>
              ))}
            </div>
          </div>

          {/* Kriterier */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-kf-gray-700">
                Kriterier ({criteria.length})
              </label>
              <button
                type="button"
                onClick={addCriterion}
                className="inline-flex items-center gap-1 px-3 py-1 text-sm text-emerald-700 hover:bg-emerald-50 rounded border border-emerald-200"
              >
                <Plus className="w-4 h-4" />
                Legg til kriterium
              </button>
            </div>

            <div className="space-y-4">
              {criteria.map((c, ci) => (
                <div key={ci} className="rounded-lg border border-kf-gray-200 p-3 space-y-3 bg-kf-gray-50">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-kf-gray-700">Kriterium {ci + 1}</span>
                    {criteria.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCriterion(ci)}
                        className="text-red-600 hover:text-red-800 text-sm inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Fjern
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-kf-gray-600 mb-1">
                        Tittel <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={c.title}
                        onChange={(e) => updateCriterion(ci, { title: e.target.value })}
                        placeholder="F.eks: Fagutførelse"
                        className="w-full px-2 py-1.5 text-sm border border-kf-gray-300 rounded bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-kf-gray-600 mb-1">Vekt (%)</label>
                      <input
                        type="number"
                        value={c.weight_pct}
                        onChange={(e) => updateCriterion(ci, { weight_pct: parseFloat(e.target.value) || 0 })}
                        min={0}
                        max={100}
                        className="w-full px-2 py-1.5 text-sm border border-kf-gray-300 rounded bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-kf-gray-600 mb-1">Beskrivelse (valgfri)</label>
                    <input
                      type="text"
                      value={c.description}
                      onChange={(e) => updateCriterion(ci, { description: e.target.value })}
                      placeholder="Kort beskrivelse av hva kriteriet måler…"
                      className="w-full px-2 py-1.5 text-sm border border-kf-gray-300 rounded bg-white"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-kf-gray-700">
                        Nivåer ({c.levels.length}) <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => addLevel(ci)}
                        className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Nivå
                      </button>
                    </div>
                    <div className="space-y-2">
                      {c.levels.map((l, li) => (
                        <div key={li} className="grid grid-cols-12 gap-2 items-start">
                          <div className="col-span-5">
                            <input
                              type="text"
                              value={l.name}
                              onChange={(e) => updateLevel(ci, li, { name: e.target.value })}
                              placeholder={`Navn (f.eks. ${li === 0 ? 'Ikke bestått' : 'Bestått'})`}
                              className="w-full px-2 py-1.5 text-sm border border-kf-gray-300 rounded bg-white"
                            />
                          </div>
                          <div className="col-span-2">
                            <input
                              type="number"
                              value={l.points}
                              onChange={(e) => updateLevel(ci, li, { points: parseFloat(e.target.value) || 0 })}
                              placeholder="Poeng"
                              className="w-full px-2 py-1.5 text-sm border border-kf-gray-300 rounded bg-white"
                            />
                          </div>
                          <div className="col-span-4">
                            <input
                              type="text"
                              value={l.description}
                              onChange={(e) => updateLevel(ci, li, { description: e.target.value })}
                              placeholder="Beskrivelse (valgfri)"
                              className="w-full px-2 py-1.5 text-sm border border-kf-gray-300 rounded bg-white"
                            />
                          </div>
                          <div className="col-span-1 flex items-center justify-end">
                            {c.levels.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeLevel(ci, li)}
                                className="text-red-600 hover:text-red-800"
                                aria-label="Fjern nivå"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 p-3 text-xs text-kf-blue-900 flex items-start gap-2">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Matrisen opprettes som <strong>utkast</strong> (ikke låst). Den låses automatisk første gang en kandidat
              sensureres mot den. Full redigering (låsing, kloning, kompleksitetsmodus) gjøres via <em>Innstillinger ▸ Vurderingsmatriser</em>.
            </div>
          </div>
        </div>

        <footer className="px-4 sm:px-6 py-3 border-t border-kf-gray-200 bg-kf-gray-50 sm:rounded-b-xl flex-shrink-0 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-3 py-1.5 text-sm text-kf-gray-700 hover:bg-white rounded"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Lagrer…' : 'Opprett matrise'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
